import { useCallback, useContext, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import AuthContext from '../../context/authContext';
import { useColorMode } from '../../context/colorModeContext';

// Base URL of the standalone FunZone B2B CRM frontend embedded below.
const CRM_URL = import.meta.env.VITE_CRM_URL || 'https://crm.fun-zone.de';
const CRM_ORIGIN = new URL(CRM_URL).origin;

// Embeds the standalone CRM frontend in a full-size iframe and hands the
// staff JWT to it via postMessage. The CRM side implements the counterpart
// of this handshake: it announces itself with 'fz-crm-ready' (or asks again
// with 'fz-crm-request-auth' / 'fz-crm-token-expired') and receives the
// token as { type: 'fz-crm-auth', token }.
//
// The colour mode travels the same way. Without it the embedded CRM would
// follow the operating system while the management tool follows the user's
// own preference — the two drift apart as soon as those differ.
function CrmView() {
  const authCtx = useContext(AuthContext);
  const token = authCtx.token;
  const { resolvedPaletteMode } = useColorMode();
  const iframeRef = useRef(null);

  // Post the current token to the CRM iframe, restricted to the CRM origin.
  const postAuthToken = useCallback(() => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (!contentWindow || !token) return;
    contentWindow.postMessage({ type: 'fz-crm-auth', token: token }, CRM_ORIGIN);
  }, [token]);

  // Send the resolved palette mode ('light' | 'dark'), never the raw
  // preference: 'system' is resolved against the management tool's own media
  // query so both sides always agree on the concrete mode.
  const postColorMode = useCallback(() => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (!contentWindow) return;
    contentWindow.postMessage({ type: 'fz-crm-theme', mode: resolvedPaletteMode }, CRM_ORIGIN);
  }, [resolvedPaletteMode]);

  // Answer handshake messages coming from the CRM iframe. Messages from any
  // other origin are ignored.
  useEffect(() => {
    const handleMessage = event => {
      if (event.origin !== CRM_ORIGIN) return;
      const type = event.data?.type;
      if (type === 'fz-crm-ready' || type === 'fz-crm-request-auth' || type === 'fz-crm-token-expired') {
        postAuthToken();
        postColorMode();
      }
      if (type === 'fz-crm-request-theme') {
        postColorMode();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [postAuthToken, postColorMode]);

  // Proactively hand the token over whenever its value changes (e.g. after a
  // re-login renewed it) — postAuthToken is recreated on every token change.
  useEffect(() => {
    postAuthToken();
  }, [postAuthToken]);

  // Push the mode whenever the user switches it, so the iframe follows without
  // a reload.
  useEffect(() => {
    postColorMode();
  }, [postColorMode]);

  return (
    // Fill the remaining viewport below the 64px app bar and cancel the
    // main content padding so the iframe runs edge to edge.
    <Box sx={{ height: 'calc(100vh - 64px)', mx: -3, mb: -3, overflow: 'hidden' }}>
      <Box
        component='iframe'
        ref={iframeRef}
        src={CRM_URL}
        title='FunZone CRM'
        onLoad={() => {
          postAuthToken();
          postColorMode();
        }}
        sx={{ display: 'block', width: '100%', height: '100%', border: 0 }}
      />
    </Box>
  );
}

export default CrmView;
